import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Flame } from "lucide-react";

/**
 * StreakWidget
 * Auto-checks-in the user on mount, then displays their current streak.
 * Designed to live in the Dashboard welcome header.
 * Silently degrades (returns null) if migration not applied.
 */
export default function StreakWidget() {
  const { token } = useAuth();
  const [streak, setStreak] = useState(null);
  const [longest, setLongest] = useState(0);
  const [migrationPending, setMigrationPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkIn = async () => {
      try {
        const res = await axios.post(`${API}/engagement/check-in`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        if (res.data?.migration_pending) {
          setMigrationPending(true);
          return;
        }
        setStreak(res.data?.streak ?? 0);
        setLongest(res.data?.longest_streak ?? 0);
      } catch (e) {
        // Silently fail — streak is non-critical
      }
    };
    if (token) checkIn();
    return () => { cancelled = true; };
  }, [token]);

  if (migrationPending || streak === null) return null;

  const flameClass = streak >= 7
    ? "text-orange-500 drop-shadow-[0_0_8px_rgba(249,115,22,0.5)]"
    : streak >= 3
      ? "text-amber-500"
      : "text-amber-300";

  const label = streak === 0
    ? "Start your streak today!"
    : streak === 1
      ? "1 day streak"
      : `${streak} day streak`;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60"
      title={longest > streak ? `Longest: ${longest} days` : undefined}
      data-testid="streak-widget"
    >
      <Flame className={`w-4 h-4 ${flameClass} ${streak >= 3 ? "animate-pulse" : ""}`} />
      <span className="text-sm font-semibold text-amber-800" data-testid="streak-count">{label}</span>
      {longest > streak && streak > 0 && (
        <span className="text-xs text-amber-600/70 ml-1">
          • best {longest}
        </span>
      )}
    </div>
  );
}
